import { useCallback, useEffect, useState } from 'react';

const usePagination = <T,>(data: T[], itemsPerPage: number, itemCount?: number) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [maxPage, setMaxPage] = useState(Math.ceil(itemCount || data.length / itemsPerPage));

  const getCurrentData = useCallback(() => {
    const page = Math.max(currentPage, 1);
    const begin = (page - 1) * itemsPerPage;
    const end = begin + itemsPerPage;
    return data.slice(begin, end);
  }, [currentPage, data, itemsPerPage]);

  const [currentData, setCurrentData] = useState(itemCount ? data : getCurrentData());

  const next = useCallback(() => {
    setCurrentPage(Math.min(currentPage + 1, maxPage));
  }, [currentPage, maxPage]);

  const prev = useCallback(() => {
    setCurrentPage(Math.max(currentPage - 1, 1));
  }, [currentPage]);

  const jump = useCallback(
    (page: number, capAtMax = false) => {
      const pageNumber = Math.max(1, page);
      if (capAtMax) {
        setCurrentPage(Math.min(pageNumber, maxPage));
      } else {
        setCurrentPage(pageNumber);
      }
    },
    [currentPage, maxPage],
  );

  useEffect(() => {
    setMaxPage(Math.ceil(itemCount || data.length / itemsPerPage));
    setCurrentData(itemCount ? data : getCurrentData());
  }, [itemCount, data, data.length, itemsPerPage, currentPage]);

  return { next, prev, jump, currentData, currentPage, maxPage };
};

export default usePagination;
